import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { Typography } from 'app/components';
import { useUpdateDocumentTaxonomyConfig, PresentationTreeNetwork, TaxonomyOverrides, LinkroleOverride } from 'entities/tag';
import { useBatchedConfigUpdate } from '../../Taxonomies/hooks';
import { linkroleLabel } from 'app/components/editor/sidebar/tags/LinkroleCell';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { TagLanguageDropdown } from 'app/components/editor/sidebar/tags/TagLanguageDropdown';
import './CategoryOverridesSection.scss';

interface CategoryOverridesSectionProps {
  configId: string;
  activeTaxonomyId: string;
  networks: PresentationTreeNetwork[];
  overrides: TaxonomyOverrides;
  canEdit: boolean;
  documentSuperId?: number;
}

interface Row {
  roleURI: string;
  definition: string;
  labels: Record<string, string>;
  alias: string;
  hidden: boolean;
}

function rowsFromState(networks: PresentationTreeNetwork[], activeTaxonomyId: string, overrides: TaxonomyOverrides): Row[] {
  const forTax = overrides[activeTaxonomyId] ?? {};
  return networks.map((n) => {
    const ov: LinkroleOverride = forTax[n.linkrole.roleURI] ?? {};
    return {
      roleURI: n.linkrole.roleURI,
      definition: n.linkrole.definition,
      labels: n.linkrole.labels ?? {},
      alias: ov.alias ?? '',
      hidden: ov.hidden === true,
    };
  });
}

function serializeForActiveTax(rows: Row[]): Record<string, LinkroleOverride> {
  const next: Record<string, LinkroleOverride> = {};
  for (const r of rows) {
    const entry: LinkroleOverride = {};
    const aliasTrim = r.alias.trim();
    if (aliasTrim.length > 0) entry.alias = aliasTrim;
    if (r.hidden) entry.hidden = true;
    if (entry.alias !== undefined || entry.hidden !== undefined) {
      next[r.roleURI] = entry;
    }
  }
  return next;
}

export const CategoryOverridesSection = ({ configId, activeTaxonomyId, networks, overrides, canEdit, documentSuperId }: CategoryOverridesSectionProps) => {
  const { t } = useTranslation('tag');
  const { tagLanguage } = useTagDisplay();

  const initial = useMemo(() => rowsFromState(networks, activeTaxonomyId, overrides), [networks, activeTaxonomyId, overrides]);
  const [rows, setRows] = useState<Row[]>(initial);
  const { mutate: updateConfig } = useUpdateDocumentTaxonomyConfig();
  const { update: scheduleSave } = useBatchedConfigUpdate({ configId, updateConfig, delay: 1000 });

  const persistRows = (next: Row[]) => {
    const nextOverrides: TaxonomyOverrides = { ...overrides, [activeTaxonomyId]: serializeForActiveTax(next) };
    scheduleSave({ taxonomyOverrides: nextOverrides });
  };

  const updateAlias = (roleURI: string, value: string) =>
    setRows((rs) => {
      const next = rs.map((r) => (r.roleURI === roleURI ? { ...r, alias: value } : r));
      persistRows(next);
      return next;
    });

  const toggleHidden = (roleURI: string) =>
    setRows((rs) => {
      const next = rs.map((r) => (r.roleURI === roleURI ? { ...r, hidden: !r.hidden } : r));
      persistRows(next);
      return next;
    });

  return (
    <section className="category-overrides">
      <header className="category-overrides__header">
        <Typography variant="body-8" fontWeight="semibold">
          {t('categoryOverrides.title')}
        </Typography>
        {documentSuperId != null && <TagLanguageDropdown documentSuperId={documentSuperId} />}
      </header>

      <div className="category-overrides__column-headers" role="presentation">
        <Typography variant="body-7" color="#717077" fontWeight="semibold">
          {t('categoryOverrides.col.category')}
        </Typography>
        <Typography variant="body-7" color="#717077" fontWeight="semibold">
          {t('categoryOverrides.col.alias')}
        </Typography>
        <span />
      </div>

      <ul className="category-overrides__list">
        {rows.map((r) => {
          const name = linkroleLabel({ uri: r.roleURI, definition: r.definition, labels: r.labels }, { tagLanguage });
          return (
            <li key={r.roleURI} className={`category-overrides__row ${r.hidden ? 'is-hidden' : ''}`}>
              <div className="category-overrides__definition" title={name}>
                <Typography variant="body-8" fontWeight="normal" color="#1c1e22">
                  {name}
                </Typography>
              </div>
              <input
                type="text"
                className="category-overrides__alias-input"
                value={r.alias}
                placeholder={name}
                maxLength={500}
                disabled={!canEdit}
                onChange={(e) => updateAlias(r.roleURI, e.target.value)}
                aria-label={`alias-${r.roleURI}`}
              />
              <button
                type="button"
                className={`category-overrides__hide-btn ${r.hidden ? 'is-hidden' : ''}`}
                onClick={() => canEdit && toggleHidden(r.roleURI)}
                disabled={!canEdit}
                title={t(r.hidden ? 'categoryOverrides.unhide' : 'categoryOverrides.hide')}
                aria-label={`hidden-${r.roleURI}`}
                aria-pressed={r.hidden}
              >
                <i className={`fa-regular ${r.hidden ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
